import { MDXEditor } from '@mdxeditor/editor';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import {
  selectedAlertRuleAtom,
  useFetchAITicketTemplates,
  useUpdateAlertRule
} from '@/pages/AlertRules/AlertRules.store';
import type { AlertRuleUpdateDTO } from '@/pages/AlertRules/AlertRules.types';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';

import useStyles from './AlertRuleEditDialog.styles';
import type { AlertRuleEditDialogType } from './AlertRuleEditDialog.types';
import { formatDefaultAlertRuleValues } from './AlertRuleEditDialog.utils';

const AlertRuleEditDialog = (props: AlertRuleEditDialogType) => {
  const { open, onClose } = props;
  const { updateAlertRule } = useUpdateAlertRule(onClose);
  const { data: aiTicketTemplates } = useFetchAITicketTemplates();
  const [selectedAlertRule] = useAtom(selectedAlertRuleAtom);
  const [selectedOrgId] = useAtom(selectedOrganizationIdAtom);
  const { classes } = useStyles();
  const { mode: themeMode } = useColorScheme();

  const defaultFormValues = formatDefaultAlertRuleValues(
    selectedAlertRule,
    selectedOrgId as number
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting }
  } = useForm<AlertRuleUpdateDTO>({
    defaultValues: defaultFormValues
  });

  const onSubmit = (data: AlertRuleUpdateDTO) => {
    const submitData =
      selectedOrgId === 0
        ? { ...data, organization_instructions: undefined }
        : data;
    updateAlertRule(submitData as AlertRuleUpdateDTO);
    // TODO: Remove on close when mutation is functional
    onClose();
  };

  const selectedTicketTemplateId = useWatch({ control, name: 'template_id' });
  const aiEnabled = useWatch({ control, name: 'ai_enabled' });

  const selectedTicketTemplateDescription = useMemo(
    () =>
      aiTicketTemplates?.find(
        (template) => template.id === Number(selectedTicketTemplateId)
      )?.summary,
    [aiTicketTemplates, selectedTicketTemplateId]
  );

  const handleCancel = () => {
    reset(); // Reset form to default values
    onClose();
  };

  const handleAiEnabledChange = (field: {
    value: boolean | null | undefined;
    onChange: (value: boolean) => void;
  }) => {
    const newValue = !field.value;
    field.onChange(newValue);
    if (!newValue) {
      setValue('ai_escalation', false);
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Alert Rule Edit Dialog</DialogTitle>
      <DialogContent className={classes.alertRuleEditDialogContent}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 3 }}>
            <InputLabel htmlFor="instructions">Global Instructions</InputLabel>
            <Stack direction="row" sx={{ alignItems: 'center' }} spacing={2}>
              <Controller
                name="instructions"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl id="instructions" fullWidth>
                    <MDXEditor
                      className={`${themeMode}-theme dark-editor ${classes.mdxEditor} ${
                        fieldState.error ? classes.error : ''
                      }`}
                      contentEditableClassName="cm-richView"
                      markdown={field.value || ''}
                      plugins={MDX_EDITOR_CONFIG}
                      suppressHtmlProcessing
                      onChange={(value) => field.onChange(value)}
                    />
                  </FormControl>
                )}
              />
            </Stack>
          </Box>
          {selectedOrgId !== 0 && (
            <Box sx={{ mb: 3 }}>
              <InputLabel htmlFor="organization_instructions">
                Organization Instructions
              </InputLabel>
              <Stack direction="row" sx={{ alignItems: 'center' }} spacing={2}>
                <Controller
                  name="organization_instructions"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl id="organization_instructions" fullWidth>
                      <MDXEditor
                        className={`${themeMode}-theme dark-editor ${classes.mdxEditor} ${
                          fieldState.error ? classes.error : ''
                        }`}
                        contentEditableClassName="cm-richView"
                        markdown={field.value || ''}
                        plugins={MDX_EDITOR_CONFIG}
                        suppressHtmlProcessing
                        onChange={(value) => field.onChange(value)}
                      />
                    </FormControl>
                  )}
                />
              </Stack>
            </Box>
          )}
          <Box sx={{ mb: 3 }}>
            <Stack spacing={2}>
              <Controller
                control={control}
                name="ai_enabled"
                render={({ field }) => (
                  <FormControlLabel
                    slotProps={{
                      typography: { sx: { fontWeight: 'bold', width: '8rem' } }
                    }}
                    control={
                      <Switch
                        checked={field.value ? field.value : false}
                        {...field}
                        onChange={() => handleAiEnabledChange(field)}
                      />
                    }
                    label="AI Analysis Enabled"
                  />
                )}
              />
              <Controller
                control={control}
                name="ai_escalation"
                render={({ field }) => (
                  <FormControlLabel
                    slotProps={{
                      typography: { sx: { fontWeight: 'bold', width: '20rem' } }
                    }}
                    control={
                      <Switch
                        checked={field.value ? field.value : false}
                        disabled={!aiEnabled}
                        {...field}
                        onChange={() => {
                          field.onChange(!field.value);
                        }}
                      />
                    }
                    label="AI Escalation (Ticketing) Enabled"
                  />
                )}
              />
            </Stack>
          </Box>
          <Box sx={{ mb: 3, mt: 3 }}>
            <InputLabel htmlFor="template_id">Ticket Template</InputLabel>
            <Stack direction="row" sx={{ alignItems: 'center' }} spacing={2}>
              <Controller
                name="template_id"
                control={control}
                render={({ field, formState }) => {
                  return (
                    <FormControl id="template_id" fullWidth>
                      <Autocomplete
                        id="category-select"
                        options={aiTicketTemplates || []}
                        getOptionLabel={(option: any) => option.name || ''}
                        value={
                          aiTicketTemplates?.find(
                            (template: any) =>
                              template.id ===
                              (field.value ? Number(field.value) : -1)
                          ) || null
                        }
                        onChange={(_, newValue) =>
                          field.onChange(newValue?.id || null)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!formState.errors.template_id}
                            placeholder="Select a template"
                          />
                        )}
                      />
                    </FormControl>
                  );
                }}
              />
            </Stack>
          </Box>
        </form>
        {selectedTicketTemplateDescription && (
          <>
            <Divider />
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
              Template Description
            </Typography>
            <Typography sx={{ mt: 1 }}>
              {selectedTicketTemplateDescription}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>

        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertRuleEditDialog;
